import React, { useState, useEffect } from 'react';
import { Routes, Route, useNavigate, useSearchParams } from 'react-router-dom';
import api from '../api/axios';
import LayoutWrapper from '../components/Layout/LayoutWrapper';
import { LoadingSpinner, ErrorHandler, EmptyState } from '../components/common/ErrorHandler';
import { getImageUrl } from '../utils/image';
import PermissionGuard, { usePermission } from '../components/Guards/PermissionGuard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Search, UserCircle, CreditCard, History, Download, FileSpreadsheet, FileText, Edit, Trash2, Printer, AlertCircle, Filter, X } from 'lucide-react';
import useToast from '../hooks/useToast';
import { generateSubscriptionReceipt } from '../components/prints/clubPrintTemplates';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';


const SubscriptionList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showSuccess, showError } = useToast();

  const [members, setMembers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState(searchParams.get('status') || 'ALL');
  const [historyModal, setHistoryModal] = useState({ open: false, member: null, history: [] });
  const [editModal, setEditModal] = useState({ open: false, payment: null });
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, paymentId: null });
  
  // Date range filter state
  const [showFilters, setShowFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  const canCreate = usePermission('subscription', 'create');
  const canUpdate = usePermission('subscription', 'update');
  const canDelete = usePermission('subscription', 'delete');

  useEffect(() => {
    fetchSubscriptions();
    fetchSummary();
  }, [activeTab, dateFrom, dateTo]);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const params = {};
      if (activeTab !== 'ALL') {
        params.status = activeTab;
      }
      if (dateFrom) {
        params.date_from = dateFrom;
      }
      if (dateTo) {
        params.date_to = dateTo;
      }
      const response = await api.get('/api/subscriptions', { params });
      setMembers(response.data);
      setError(null);
    } catch (err) {
      setError(err);
      showError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchSummary = async () => {
    try {
      const params = {};
      if (dateFrom) {
        params.date_from = dateFrom;
      }
      if (dateTo) {
        params.date_to = dateTo;
      }
      const response = await api.get('/api/subscriptions/summary', { params });
      setSummary(response.data);
    } catch (err) {
      console.error('Failed to fetch summary', err);
    }
  };

  const handleViewHistory = async (member) => {
    try {
      const params = {};
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await api.get(`/api/subscriptions/member/${member.id}`, { params });
      setHistoryModal({ open: true, member, history: response.data });
    } catch (err) {
      showError('Failed to load payment history');
    }
  };

  const handlePrintReceipt = (payment, member = null) => {
    try {
      const memberData = member || historyModal.member;
      const doc = generateSubscriptionReceipt(payment, memberData);
      doc.save(`subscription_receipt_${payment.receipt_no}.pdf`);
      showSuccess('Receipt downloaded successfully');
    } catch (err) {
      showError('Failed to generate receipt');
      console.error(err);
    }
  };

  const handleEditPayment = (payment) => {
    setEditModal({ open: true, payment: { ...payment } });
  };

  const handleUpdatePayment = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/api/subscriptions/${editModal.payment.id}`, {
        payment_amount: parseFloat(editModal.payment.payment_amount),
        payment_mode: editModal.payment.payment_mode,
        payment_date: editModal.payment.payment_date
      });
      showSuccess('Payment updated successfully');
      setEditModal({ open: false, payment: null });
      if (historyModal.member) {
        handleViewHistory(historyModal.member);
      }
      fetchSubscriptions();
      fetchSummary();
    } catch (err) {
      showError(err.message);
    }
  };

  const handleDeletePayment = async () => {
    try {
      await api.delete(`/api/subscriptions/${deleteConfirm.paymentId}`);
      showSuccess('Payment deleted successfully');
      setDeleteConfirm({ open: false, paymentId: null });
      if (historyModal.member) {
        handleViewHistory(historyModal.member);
      }
      fetchSubscriptions();
      fetchSummary();
    } catch (err) {
      showError(err.message);
    }
  };

  const handleTabChange = (value) => {
    setActiveTab(value);
    setSearchParams(value === 'ALL' ? {} : { status: value });
  };

  const handleClearFilters = () => {
    setDateFrom('');
    setDateTo('');
    setShowFilters(false);
  };

  const exportToExcel = () => {
    try {
      const exportData = filteredMembers.map((member, index) => {
        const dues = member.subscription_dues || {};
        return {
          'SL NO': index + 1,
          'MEMBER NO': member.membership_no,
          'NAME': member.applicant_name,
          'MOBILE': member.mobile,
          'JOIN DATE': member.date,
          'AGE': dues.age || 0,
          'STATUS': dues.status === 'FREE' ? `FREE (${dues.age}Y)` : dues.status,
          'PENDING MONTHS': dues.pending_months || 0,
          'PENDING AMOUNT': dues.pending || 0,
          'TOTAL PAID': dues.total_paid || 0
        };
      });

      const ws = XLSX.utils.json_to_sheet(exportData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Subscriptions');

      const maxWidth = exportData.reduce((w, r) => Math.max(w, r.NAME?.length || 10), 10);
      ws['!cols'] = [
        { wch: 6 }, { wch: 12 }, { wch: maxWidth + 2 }, { wch: 12 },
        { wch: 12 }, { wch: 6 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 12 }
      ];

      const dateStr = new Date().toISOString().split('T')[0];
      XLSX.writeFile(wb, `Subscription_Report_${dateStr}.xlsx`);
      showSuccess('Excel report exported successfully');
    } catch (err) {
      showError('Failed to export Excel');
      console.error(err);
    }
  };

  const exportToPDF = () => {
    try {
      const doc = new jsPDF();

      doc.setFontSize(16);
      doc.text('WILSON GARDEN CLUB (R)', 105, 15, { align: 'center' });
      doc.setFontSize(10);
      doc.text('NO. 35/2, 9TH CROSS, WILSON GARDEN', 105, 22, { align: 'center' });
      doc.text('BANGALORE – 560027', 105, 28, { align: 'center' });
      doc.text('PHONE: 080-4130-0190', 105, 34, { align: 'center' });

      doc.setFontSize(14);
      doc.text('Subscription Report - Permanent Members', 105, 45, { align: 'center' });

      doc.setFontSize(9);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 55);
      if (dateFrom || dateTo) {
        doc.text(`Date Range: ${dateFrom || 'Start'} to ${dateTo || 'End'}`, 14, 60);
      }

      const tableData = filteredMembers.map((member, index) => {
        const dues = member.subscription_dues || {};
        return [
          index + 1,
          member.membership_no,
          member.applicant_name,
          member.mobile,
          member.date,
          dues.age || 0,
          dues.status === 'FREE' ? `FREE (${dues.age}Y)` : dues.status,
          dues.pending_months || 0,
          `${(dues.pending || 0).toLocaleString()}`,
          `${(dues.total_paid || 0).toLocaleString()}`
        ];
      });

      autoTable(doc, {
        startY: dateFrom || dateTo ? 65 : 60,
        head: [['#', 'Member No', 'Name', 'Mobile', 'Join Date', 'Age', 'Status', 'Pending Months', 'Pending', 'Total Paid']],
        body: tableData,
        theme: 'grid',
        headStyles: { fillColor: [41, 128, 185], fontSize: 8 },
        bodyStyles: { fontSize: 8 },
        columnStyles: {
          0: { cellWidth: 10 }, 1: { cellWidth: 20 }, 2: { cellWidth: 35 },
          3: { cellWidth: 20 }, 4: { cellWidth: 20 }, 5: { cellWidth: 12 },
          6: { cellWidth: 18 }, 7: { cellWidth: 20 }, 8: { cellWidth: 20 }, 9: { cellWidth: 20 }
        }
      });

      const dateStr = new Date().toISOString().split('T')[0];
      doc.save(`Subscription_Report_${dateStr}.pdf`);
      showSuccess('PDF report exported successfully');
    } catch (err) {
      showError('Failed to export PDF');
      console.error(err);
    }
  };

  const filteredMembers = members.filter(m =>
    !search ||
    m.membership_no?.toLowerCase().includes(search.toLowerCase()) ||
    m.applicant_name?.toLowerCase().includes(search.toLowerCase()) ||
    m.mobile?.includes(search)
  );

  if (loading) {
    return <LoadingSpinner text="Loading subscriptions..." />;
  }

  return (
    <>
      {/* Summary Cards with custom gradient colors */}
      {summary && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6 px-2 sm:px-0">
  {/* Total Pending */}
  <Card className="bg-gradient-to-br from-indigo-600 to-indigo-700 text-white shadow-md hover:shadow-lg transition-shadow">
    <CardContent className="py-4">
      <p className="text-xs sm:text-sm text-white/80 mb-1">
        Total Pending
      </p>
      <p className="text-xl sm:text-2xl font-semibold tracking-tight">
        {summary.total_pending?.toLocaleString()}
      </p>
    </CardContent>
  </Card>

  {/* Total Collected */}
  <Card className="bg-gradient-to-br from-rose-500 to-rose-600 text-white shadow-md hover:shadow-lg transition-shadow">
    <CardContent className="py-4">
      <p className="text-xs sm:text-sm text-white/80 mb-1">
        Total Collected
      </p>
      <p className="text-xl sm:text-2xl font-semibold tracking-tight">
        {summary.total_collected?.toLocaleString()}
      </p>
    </CardContent>
  </Card>

  {/* Total Members */}
  <Card className="bg-gradient-to-br from-sky-500 to-sky-600 text-white shadow-md hover:shadow-lg transition-shadow">
    <CardContent className="py-4">
      <p className="text-xs sm:text-sm text-white/80 mb-1">
        Total Members
      </p>
      <p className="text-xl sm:text-2xl font-semibold tracking-tight">
        {summary.total_members}
      </p>
    </CardContent>
  </Card>

  {/* Free Members */}
  <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-md hover:shadow-lg transition-shadow">
    <CardContent className="py-4">
      <p className="text-xs sm:text-sm text-white/80 mb-1">
        Free (65+)
      </p>
      <p className="text-xl sm:text-2xl font-semibold tracking-tight">
        {summary.free_members}
      </p>
    </CardContent>
  </Card>
</div>

      )}

      {error && <ErrorHandler error={error} onRetry={fetchSubscriptions} />}

      {/* Main List Card */}
      <Card className="shadow-lg mx-2 sm:mx-0">
        <CardHeader className="border-b bg-gradient-to-r from-gray-50 to-white">
          <div className="flex flex-col gap-3 sm:gap-4">
            <div className="flex flex-col lg:flex-row gap-3 sm:gap-4 justify-between items-stretch lg:items-center">
              <Tabs value={activeTab} onValueChange={handleTabChange} className="w-full lg:w-auto">
                <TabsList className="grid w-full grid-cols-4 lg:w-auto">
                  <TabsTrigger
  value="ALL"
  className="
    text-xs sm:text-sm
    transition-colors
    data-[state=active]:bg-blue-700
    data-[state=active]:text-white
    data-[state=active]:font-semibold
    data-[state=active]:shadow-sm
  "
>
  All
</TabsTrigger>

<TabsTrigger
  value="PENDING"
  className="
    text-xs sm:text-sm
    transition-colors
    data-[state=active]:bg-blue-700
    data-[state=active]:text-white
    data-[state=active]:font-semibold
    data-[state=active]:shadow-sm
  "
>
  Pending
</TabsTrigger>

<TabsTrigger
  value="PAID"
  className="
    text-xs sm:text-sm
    transition-colors
    data-[state=active]:bg-blue-700
    data-[state=active]:text-white
    data-[state=active]:font-semibold
    data-[state=active]:shadow-sm
  "
>
  Paid
</TabsTrigger>

<TabsTrigger
  value="FREE"
  className="
    text-xs sm:text-sm
    transition-colors
    data-[state=active]:bg-blue-700
    data-[state=active]:text-white
    data-[state=active]:font-semibold
    data-[state=active]:shadow-sm
  "
>
  Free
</TabsTrigger>

                </TabsList>
              </Tabs>
              <div className="flex flex-col sm:flex-row gap-2 w-full lg:w-auto">
                <div className="relative flex-1 sm:flex-initial sm:w-48 lg:w-64">
  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />

  <Input
    placeholder="Search..."
    value={search}
    onChange={(e) => setSearch(e.target.value)}
    className="h-10 !pl-10 pr-3 py-0 text-sm"
  />
</div>

                <div className="flex gap-2">
                  <Button
  variant="outline"
  size="sm"
  onClick={() => setShowFilters(!showFilters)}
  className={`
    flex-1 sm:flex-initial
    text-sm
    transition-colors
    ${showFilters || dateFrom || dateTo
      ? "bg-blue-700 text-white border-blue-700 hover:bg-blue-800"
      : "hover:bg-blue-50 hover:text-blue-700"}
  `}
>
  <Filter className="w-4 h-4 sm:mr-2" />
  <span className="hidden sm:inline">Filters</span>
</Button>

                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={exportToExcel} 
                    data-testid="export-excel-btn" 
                    className="flex-1 sm:flex-initial transition-all duration-200 hover:scale-105 hover:bg-green-50 hover:border-green-500 hover:text-green-700 active:scale-95"
                  >
                    <FileSpreadsheet className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline text-sm">Excel</span>
                  </Button>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={exportToPDF} 
                    data-testid="export-pdf-btn" 
                    className="flex-1 sm:flex-initial transition-all duration-200 hover:scale-105 hover:bg-red-50 hover:border-red-500 hover:text-red-700 active:scale-95"
                  >
                    <FileText className="w-4 h-4 sm:mr-2" />
                    <span className="hidden sm:inline text-sm">PDF</span>
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Date Range Filter */}
            {showFilters && (
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200 shadow-inner">
                <div className="flex-1">
                  <Label htmlFor="date-from" className="text-xs sm:text-sm font-medium text-gray-700">From Date</Label>
                  <Input
                    id="date-from"
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="mt-1 text-sm"
                  />
                </div>
                <div className="flex-1">
                  <Label htmlFor="date-to" className="text-xs sm:text-sm font-medium text-gray-700">To Date</Label>
                  <Input
                    id="date-to"
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="mt-1 text-sm"
                  />
                </div>
                <div className="flex items-end">
                  <Button 
                    variant="ghost" 
                    size="sm" 
                    onClick={handleClearFilters}
                    className="w-full sm:w-auto transition-colors duration-200 hover:bg-red-100 hover:text-red-700"
                  >
                    <X className="w-4 h-4 mr-2" />
                    Clear
                  </Button>
                </div>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {filteredMembers.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="w-10 sm:w-12 text-xs font-semibold">#</TableHead>
                    <TableHead className="w-12 sm:w-16 text-xs font-semibold">Photo</TableHead>
                    <TableHead className="min-w-[100px] sm:min-w-[120px] text-xs font-semibold">Member No</TableHead>
                    <TableHead className="min-w-[120px] sm:min-w-[150px] text-xs font-semibold">Name</TableHead>
                    <TableHead className="min-w-[100px] sm:min-w-[120px] text-xs font-semibold">Mobile</TableHead>
                    <TableHead className="min-w-[90px] sm:min-w-[100px] text-xs font-semibold">Join Date</TableHead>
                    <TableHead className="min-w-[80px] sm:min-w-[100px] text-xs font-semibold">Status</TableHead>
                    <TableHead className="min-w-[90px] sm:min-w-[100px] text-xs font-semibold">Pending</TableHead>
                    <TableHead className="min-w-[90px] sm:min-w-[100px] text-xs font-semibold">Total Paid</TableHead>
                    <TableHead className="min-w-[140px] sm:min-w-[160px] text-xs font-semibold">Action</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMembers.map((member, index) => {
                    const dues = member.subscription_dues || {};
                    return (
                      <TableRow key={member.id} className="hover:bg-gray-50 transition-colors duration-150">
                        <TableCell className="text-xs sm:text-sm">{index + 1}</TableCell>
                        <TableCell>
                          {member.profile_picture ? (
                            <img
                              src={member.profile_picture.startsWith('http')
                                ? member.profile_picture
                                : `${process.env.REACT_APP_BACKEND_URL}${member.profile_picture}`}
                              alt={member.applicant_name}
                              className="w-8 h-8 sm:w-10 sm:h-10 rounded-full object-cover border-2 border-gray-200"
                            />
                          ) : (
                            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center">
                              <UserCircle className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
                            </div>
                          )}
                        </TableCell>
                        <TableCell className="font-medium text-xs sm:text-sm">{member.membership_no}</TableCell>
                        <TableCell className="text-xs sm:text-sm">{member.applicant_name}</TableCell>
                        <TableCell className="text-xs sm:text-sm">{member.mobile}</TableCell>
                        <TableCell className="text-xs sm:text-sm">{member.date}</TableCell>
                        <TableCell>
                          <Badge 
                            variant={
                              dues.status === 'FREE' ? 'secondary' :
                              dues.status === 'PAID' ? 'success' : 'destructive'
                            }
                            className="text-xs whitespace-nowrap"
                          >
                            {dues.status === 'FREE' ? `FREE (${dues.age}Y)` : dues.status}
                            {dues.status === 'PENDING' && ` (${dues.pending_months}M)`}
                          </Badge>
                        </TableCell>
                        <TableCell className={`text-xs sm:text-sm font-medium ${dues.pending > 0 ? 'text-red-600' : 'text-green-600'}`}>
                          {(dues.pending || 0).toLocaleString()}
                        </TableCell>
                        <TableCell className="text-xs sm:text-sm">{(dues.total_paid || 0).toLocaleString()}</TableCell>
                        <TableCell>
                          <div className="flex flex-wrap gap-1.5 sm:gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleViewHistory(member)}
                              data-testid={`history-btn-${member.membership_no}`}
                              title="View History"
                              className="h-8 px-2 sm:px-3 transition-all duration-200 hover:bg-blue-50 hover:border-blue-500 hover:text-blue-700 hover:shadow-md active:scale-95"
                            >
                              <History className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                            </Button>
                            {canCreate && dues.status === 'PENDING' && dues.pending > 0 && (
                              <Button
  size="sm"
  onClick={() => navigate(`/subscription/entry?member=${member.id}`)}
  data-testid={`pay-btn-${member.membership_no}`}
  className="
    h-8 px-2 sm:px-3
    text-xs font-semibold
    bg-green-600 text-white
    border border-green-600
    hover:bg-green-700
    hover:shadow-md
    transition-all duration-200
    active:scale-95
  "
>
  Pay
</Button>

                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="py-12">
              <EmptyState
                icon={CreditCard}
                title="No members found"
                description="No permanent members match your criteria"
              />
            </div>
          )}
        </CardContent>
      </Card>

      {/* History Modal - With White Background */}
      <Dialog open={historyModal.open} onOpenChange={(open) => !open && setHistoryModal({ open: false, member: null, history: [] })}>
        <DialogContent 
          className="w-[96vw] sm:w-[90vw] md:w-[85vw] lg:w-[80vw] max-w-6xl h-[90vh] max-h-[90vh] bg-white overflow-hidden flex flex-col p-0"
          style={{ backgroundColor: '#ffffff' }}
          data-testid="payment-history-modal"
        >
          <DialogHeader className="border-b bg-white px-4 sm:px-6 py-4">
            <DialogTitle className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900">
              Payment History - {historyModal.member?.applicant_name} ({historyModal.member?.membership_no})
            </DialogTitle>
          </DialogHeader>
          <div className="flex-1 overflow-y-auto bg-white px-2 sm:px-4 lg:px-6 py-4">
            {historyModal.history.length > 0 ? (
              <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
                <Table>
                  <TableHeader>
                    <TableRow className="bg-gradient-to-r from-gray-100 to-gray-50 border-b-2 border-gray-300">
                      <TableHead className="px-3 py-3 text-xs sm:text-sm font-bold text-gray-700">Date</TableHead>
                      <TableHead className="px-3 py-3 text-xs sm:text-sm font-bold text-gray-700">Amount</TableHead>
                      <TableHead className="px-3 py-3 text-xs sm:text-sm font-bold text-gray-700">Receipt No</TableHead>
                      <TableHead className="px-3 py-3 text-xs sm:text-sm font-bold text-gray-700">Mode</TableHead>
                      <TableHead className="px-3 py-3 text-xs sm:text-sm font-bold text-gray-700 text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {historyModal.history.map((payment) => (
                      <TableRow key={payment.id} className="bg-white hover:bg-blue-50 transition-colors duration-200 border-b border-gray-100">
                        <TableCell className="px-3 py-3 text-xs sm:text-sm text-gray-800">{payment.payment_date}</TableCell>
                        <TableCell className="px-3 py-3 text-xs sm:text-sm font-semibold text-green-700">{(payment.payment_amount || 0).toLocaleString()}</TableCell>
                        <TableCell className="px-3 py-3 text-xs sm:text-sm text-gray-700">{payment.receipt_no}</TableCell>
                        <TableCell className="px-3 py-3 text-xs sm:text-sm text-gray-700">
                          <Badge variant="outline" className="text-xs">{payment.payment_mode}</Badge>
                        </TableCell>
                        <TableCell className="px-3 py-3">
                          <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handlePrintReceipt(payment)}
                              data-testid={`print-payment-${payment.id}`}
                              title="Print Receipt"
                              className="h-8 px-2 sm:px-3 text-xs bg-white transition-all duration-200 hover:bg-purple-50 hover:border-purple-500 hover:text-purple-700 hover:shadow-lg active:scale-95"
                            >
                              <Printer className="w-3.5 h-3.5 sm:mr-1" />
                              <span className="hidden sm:inline">Print</span>
                            </Button>
                            {canUpdate && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleEditPayment(payment)}
                                data-testid={`edit-payment-${payment.id}`}
                                title="Edit Payment"
                                className="h-8 px-2 sm:px-3 bg-white transition-all duration-200 hover:bg-blue-50 hover:border-blue-500 hover:text-blue-700 hover:shadow-lg active:scale-95"
                              >
                                <Edit className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {canDelete && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => setDeleteConfirm({ open: true, paymentId: payment.id })}
                                data-testid={`delete-payment-${payment.id}`}
                                title="Delete Payment"
                                className="h-8 px-2 sm:px-3 bg-white transition-all duration-200 hover:bg-red-50 hover:border-red-500 hover:text-red-700 hover:shadow-lg active:scale-95"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            ) : (
              <div className="py-16 text-center bg-white">
                <AlertCircle className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-sm sm:text-base text-gray-500 font-medium">No payment history found</p>
              </div>
            )}
          </div>
          <div className="border-t bg-white px-4 sm:px-6 py-3 flex justify-end">
            <Button 
              variant="outline" 
              onClick={() => setHistoryModal({ open: false, member: null, history: [] })}
              className="transition-all duration-200 hover:bg-gray-100 active:scale-95"
            >
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Payment Modal - With White Background */}
      <Dialog open={editModal.open} onOpenChange={(open) => !open && setEditModal({ open: false, payment: null })}>
        <DialogContent 
          data-testid="edit-payment-modal" 
          className="w-[95vw] sm:w-full sm:max-w-[500px] max-h-[95vh] overflow-y-auto bg-white p-0"
          style={{ backgroundColor: '#ffffff' }}
        >
          <DialogHeader className="border-b bg-white px-4 sm:px-6 py-4">
            <DialogTitle className="text-base sm:text-lg font-semibold text-gray-900">Edit Payment</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdatePayment} className="bg-white">
            <div className="space-y-5 px-4 sm:px-6 py-6">
              <div>
                <Label htmlFor="edit-date" className="text-sm font-semibold text-gray-700 mb-2 block">Payment Date</Label>
                <Input
                  id="edit-date"
                  type="date"
                  value={editModal.payment?.payment_date || ''}
                  onChange={(e) => setEditModal(prev => ({
                    ...prev,
                    payment: { ...prev.payment, payment_date: e.target.value }
                  }))}
                  className="text-sm bg-white border-gray-300"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-amount" className="text-sm font-semibold text-gray-700 mb-2 block">Payment Amount ()</Label>
                <Input
                  id="edit-amount"
                  type="number"
                  step="200"
                  value={editModal.payment?.payment_amount || ''}
                  onChange={(e) => setEditModal(prev => ({
                    ...prev,
                    payment: { ...prev.payment, payment_amount: e.target.value }
                  }))}
                  className="text-sm bg-white border-gray-300"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-mode" className="text-sm font-semibold text-gray-700 mb-2 block">Payment Mode</Label>
                <Select
                  value={editModal.payment?.payment_mode || 'Cash'}
                  onValueChange={(value) => setEditModal(prev => ({
                    ...prev,
                    payment: { ...prev.payment, payment_mode: value }
                  }))}
                >
                  <SelectTrigger className="bg-white border-gray-300">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="Cash">Cash</SelectItem>
                    <SelectItem value="Card">Card</SelectItem>
                    <SelectItem value="UPI">UPI</SelectItem>
                    <SelectItem value="Net Banking">Net Banking</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 px-4 sm:px-6 py-4 border-t bg-white">
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setEditModal({ open: false, payment: null })} 
                className="w-full sm:w-auto transition-all duration-200 hover:bg-gray-100 active:scale-95"
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                data-testid="save-edit-payment-btn" 
                className="w-full sm:w-auto transition-all duration-200 hover:shadow-lg active:scale-95"
              >
                Save Changes
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Modal - With White Background */}
      <Dialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, paymentId: null })}>
        <DialogContent 
          data-testid="delete-confirm-modal" 
          className="w-[95vw] sm:w-full sm:max-w-[450px] bg-white p-0"
          style={{ backgroundColor: '#ffffff' }}
        >
          <DialogHeader className="border-b bg-white px-4 sm:px-6 py-4">
            <DialogTitle className="text-base sm:text-lg font-semibold text-gray-900 flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-red-600" />
              Confirm Delete
            </DialogTitle>
          </DialogHeader>
          <div className="px-4 sm:px-6 py-6 bg-white">
            <p className="text-sm sm:text-base text-gray-700 leading-relaxed">
              Are you sure you want to delete this payment record? This action cannot be undone.
            </p>
          </div>
          <DialogFooter className="flex flex-col-reverse sm:flex-row gap-2 px-4 sm:px-6 py-4 border-t bg-white">
            <Button 
              variant="outline" 
              onClick={() => setDeleteConfirm({ open: false, paymentId: null })} 
              className="w-full sm:w-auto transition-all duration-200 hover:bg-gray-100 active:scale-95"
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeletePayment} 
              data-testid="confirm-delete-btn" 
              className="w-full sm:w-auto transition-all duration-200 hover:shadow-lg active:scale-95"
            >
              Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const SubscriptionEntry = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const memberId = searchParams.get('member');
  const { showSuccess, showError } = useToast();

  const [search, setSearch] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedMember, setSelectedMember] = useState(null);
  const [dues, setDues] = useState(null);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMode, setPaymentMode] = useState('Cash');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (memberId) {
      fetchMemberDues(memberId);
    }
  }, [memberId]);

  const handleSearch = async (query) => {
    setSearch(query);
    if (query.length < 2) {
      setSearchResults([]);
      return;
    }

    try {
      const response = await api.get('/api/members/search/autocomplete', {
        params: { q: query }
      });
      setSearchResults(response.data.filter(m => m.membership_type === 'Permanent'));
    } catch (err) {
      console.error('Search failed', err);
    }
  };

  const fetchMemberDues = async (id) => {
    try {
      const response = await api.get(`/api/subscriptions/member/${id}/dues`);
      setSelectedMember(response.data.member);
      setDues(response.data.dues);
      setSearch('');
      setSearchResults([]);
    } catch (err) {
      showError('Failed to load member details');
    }
  };

  const handleSelectMember = (member) => {
    fetchMemberDues(member.id);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedMember) {
      showError('Please select a member');
      return;
    }

    const amount = parseFloat(paymentAmount);
    if (!amount || amount <= 0) {
      showError('Please enter a valid amount');
      return;
    }

    if (amount % 200 !== 0) {
      showError('Payment amount must be in multiples of 200');
      return;
    }

    if (amount > dues?.pending) {
      showError(`Amount cannot exceed pending amount of ${dues.pending}`);
      return;
    }

    try {
      setSaving(true);
      const response = await api.post('/api/subscriptions', {
        member_id: selectedMember.id,
        payment_date: new Date().toISOString().split('T')[0],
        payment_amount: amount,
        payment_mode: paymentMode
      });

      showSuccess('Subscription payment recorded successfully');

      const doc = generateSubscriptionReceipt(response.data, selectedMember);
      doc.save(`subscription_receipt_${response.data.receipt_no}.pdf`);

      navigate('/subscription');
    } catch (err) {
      showError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-3 sm:px-4 lg:px-6">
      <Card className="mb-4 sm:mb-6 shadow-lg">
        <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
          <CardTitle className="text-base sm:text-lg lg:text-xl">Search Member</CardTitle>
        </CardHeader>
        <CardContent className="pt-4 sm:pt-6">
          <div className="relative">
            <div className="relative w-full">
  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none z-10" />

  <Input
    placeholder="Search by Name / Member No / Mobile"
    value={search}
    onChange={(e) => handleSearch(e.target.value)}
    data-testid="member-search-input"
    className="h-10 !pl-11 pr-3 py-0 text-sm"
  />
</div>


            {searchResults.length > 0 && (
              <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                {searchResults.map((member) => (
                  <button
                    key={member.id}
                    onClick={() => handleSelectMember(member)}
                    className="w-full px-3 sm:px-4 py-2.5 sm:py-3 text-left hover:bg-gradient-to-r hover:from-blue-50 hover:to-indigo-50 flex items-center gap-3 border-b last:border-0 transition-all duration-200 active:bg-blue-100"
                    data-testid={`search-result-${member.membership_no}`}
                  >
                    {member.profile_picture ? (
                     <img src={getImageUrl(member.profile_picture)} alt="" className="w-9 h-9 sm:w-10 sm:h-10 rounded-full object-cover flex-shrink-0 border-2 border-gray-200" />
                    ) : (
                      <div className="w-9 h-9 sm:w-10 sm:h-10 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                        <UserCircle className="w-5 h-5 sm:w-6 sm:h-6 text-gray-400" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate text-sm sm:text-base">{member.applicant_name}</p>
                      <p className="text-xs sm:text-sm text-gray-500">{member.membership_no}</p>
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {!selectedMember && (
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200 shadow-lg">
          <CardContent className="py-8 sm:py-12">
            <div className="flex flex-col items-center justify-center text-center px-4">
              <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-blue-500 mb-3 sm:mb-4" />
              <h3 className="text-base sm:text-lg font-semibold text-blue-900 mb-2">No Member Selected</h3>
              <p className="text-sm sm:text-base text-blue-700 max-w-md">
                Please enter member name above to take entry
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {selectedMember && dues && (
        <form onSubmit={handleSubmit}>
          <Card className="mb-4 sm:mb-6 shadow-lg">
            <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
              <CardTitle className="text-base sm:text-lg lg:text-xl">Member Details</CardTitle>
            </CardHeader>
            <CardContent className="pt-4 sm:pt-6">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                {selectedMember.profile_picture ? (
                 <img src={getImageUrl(selectedMember.profile_picture)}alt="" className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover flex-shrink-0 border-2 border-gray-200" />
                ) : (
                  <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-gray-200 to-gray-300 rounded-full flex items-center justify-center flex-shrink-0">
                    <UserCircle className="w-8 h-8 sm:w-10 sm:h-10 text-gray-400" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <h3 className="text-base sm:text-lg font-bold truncate" data-testid="selected-member-name">{selectedMember.applicant_name}</h3>
                  <p className="text-gray-500 text-xs sm:text-sm lg:text-base">{selectedMember.membership_no} • {selectedMember.mobile}</p>
                  <p className="text-xs sm:text-sm text-gray-400">Joined: {selectedMember.date}</p>
                </div>
                <Badge className="ml-0 sm:ml-auto text-xs sm:text-sm" variant={dues.status === 'FREE' ? 'secondary' : dues.status === 'PAID' ? 'success' : 'destructive'}>
                  {dues.status}
                </Badge>
              </div>

              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-br from-gray-50 to-blue-50 rounded-lg border border-gray-200">
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Monthly Fee</p>
                  <p className="font-bold text-sm sm:text-base mt-1">200</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Total Due</p>
                  <p className="font-bold text-sm sm:text-base mt-1" data-testid="total-due">{(dues.total_due || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Total Paid</p>
                  <p className="font-bold text-green-600 text-sm sm:text-base mt-1" data-testid="total-paid">{(dues.total_paid || 0).toLocaleString()}</p>
                </div>
                <div>
                  <p className="text-xs sm:text-sm text-gray-600">Pending</p>
                  <p className="font-bold text-red-600 text-sm sm:text-base mt-1" data-testid="pending-amount">{(dues.pending || 0).toLocaleString()}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {dues.status !== 'FREE' && dues.pending > 0 && (
            <Card className="shadow-lg">
              <CardHeader className="bg-gradient-to-r from-gray-50 to-white border-b">
                <CardTitle className="text-base sm:text-lg lg:text-xl">Payment Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-4 sm:pt-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="amount" className="text-sm font-medium">Payment Amount () *</Label>
                    <Input
                      id="amount"
                      type="number"
                      step="200"
                      max={dues.pending}
                      value={paymentAmount}
                      onChange={(e) => setPaymentAmount(e.target.value)}
                      placeholder={`Max: ${dues.pending}`}
                      data-testid="payment-amount-input"
                      className="mt-1 text-sm"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">Maximum: {dues.pending.toLocaleString()} (Multiples of 200)</p>
                  </div>
                  <div>
                    <Label htmlFor="mode" className="text-sm font-medium">Payment Mode</Label>
                    <Select value={paymentMode} onValueChange={setPaymentMode}>
                      <SelectTrigger data-testid="payment-mode-select" className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Cash">Cash</SelectItem>
                        <SelectItem value="Card">Card</SelectItem>
                        <SelectItem value="UPI">UPI</SelectItem>
                        <SelectItem value="Net Banking">Net Banking</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-4 pt-4 border-t">
                  <Button 
                    type="button" 
                    variant="outline" 
                    onClick={() => navigate('/subscription')} 
                    className="w-full sm:w-auto transition-all duration-200 hover:bg-gray-100 active:scale-95"
                  >
                    Cancel
                  </Button>
                  <Button 
                    type="submit" 
                    disabled={saving} 
                    data-testid="pay-and-print-btn" 
                    className="w-full sm:w-auto transition-all duration-200 hover:shadow-md active:scale-95"
                  >
                    {saving ? 'Processing...' : 'Save Receipt'}
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {dues.status !== 'FREE' && dues.pending === 0 && (
            <Card className="bg-gradient-to-br from-green-50 to-emerald-50 border-green-300 shadow-lg">
              <CardContent className="py-8 sm:py-12 text-center">
                <p className="text-sm sm:text-base text-green-800 font-medium" data-testid="no-pending-dues-message">
                  ✅ No pending subscription dues.<br />This member is fully paid up to date.
                </p>
              </CardContent>
            </Card>
          )}

          {dues.status === 'FREE' && (
            <Card className="bg-gradient-to-br from-amber-50 to-yellow-50 border-amber-300 shadow-lg">
              <CardContent className="py-8 sm:py-12 text-center">
                <p className="text-sm sm:text-base text-amber-800 font-medium" data-testid="free-member-message">
                  This member is {dues.age} years old and qualifies for FREE subscription
                </p>
              </CardContent>
            </Card>
          )}
        </form>
      )}
    </div>
  );
};

const Subscription = () => {
  return (
    <LayoutWrapper
      title="Subscription Management"
      subtitle="Manage permanent member subscriptions"
    >
      <Routes>
        <Route index element={<SubscriptionList />} />
        <Route path="entry" element={<SubscriptionEntry />} />
      </Routes>
    </LayoutWrapper>
  );
};

export default Subscription;
